import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, Menu, X, MapPin, Heart, Store as StoreIcon, Camera, Image as ImageIcon, User } from 'lucide-react';
import VisualSearchModal from '@/components/VisualSearchModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useCart } from '@/context/CartContext';
import { CATEGORIES } from '@/mock/products';
import { useSelectedStore, useWishlist, useCategories } from '@/store/dataStore';
import { useLabels } from '@/services/labelsService';
import Logo from './Logo';
import StoreSelector from './StoreSelector';

export default function Header() {
  const { count, setIsOpen } = useCart();
  const { store } = useSelectedStore();
  const { ids: favIds } = useWishlist();
  const { categories } = useCategories();
  const CATS = categories && categories.length ? categories : CATEGORIES;
  const { label: L } = useLabels();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showStoreSelector, setShowStoreSelector] = useState(false);
  const [query, setQuery] = useState('');
  const [visualOpen, setVisualOpen] = useState(null); // 'barcode' | 'photo' | null
  const navigate = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    if (query.trim()) navigate(`/catalogo?q=${encodeURIComponent(query.trim())}`);
  };
  const onScanned = (searchValue) => {
    setVisualOpen(null);
    if (searchValue) navigate(`/catalogo?q=${encodeURIComponent(searchValue)}`);
  };

  return (
    <header className="sticky top-0 z-40 bg-[#1B2A4A] text-white shadow-md">
      {/* Top bar */}
      <div className="bg-[#141f38] text-xs text-yellow-100/80">
        <div className="max-w-7xl mx-auto px-4 py-1.5 flex items-center justify-between gap-2">
          <button onClick={() => setShowStoreSelector(true)} className="flex items-center gap-1.5 hover:text-yellow-300 transition">
            <MapPin size={12} /> {store ? <span className="font-semibold">{store.name}</span> : 'Elige tu tienda'}
          </button>
          <span className="hidden md:inline">Envíos • Pedidos por WhatsApp {store ? `• +${store.whatsapp}` : ''}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 md:px-4 py-3 flex items-center gap-2 md:gap-3">
        <button className="lg:hidden shrink-0" onClick={() => setMobileOpen(true)} aria-label="menu">
          <Menu size={26} />
        </button>

        <Link to="/" className="flex-shrink-0">
          <Logo className="h-8 md:h-11" />
        </Link>

        <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-2xl mx-6 relative">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="¿Qué estás buscando hoy?"
            className="pl-11 pr-24 h-11 bg-white text-slate-900 border-0 rounded-full focus-visible:ring-2 focus-visible:ring-yellow-300"
          />
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
            <button type="button" onClick={() => setVisualOpen('photo')} data-testid="btn-visual-search" title="Buscar por foto" className="p-1.5 text-slate-500 hover:text-[#1B2A4A] hover:bg-yellow-100 rounded-full transition"><ImageIcon size={18} /></button>
            <button type="button" onClick={() => setVisualOpen('barcode')} data-testid="btn-barcode-search" title="Escanear código de barras" className="p-1.5 text-slate-500 hover:text-[#1B2A4A] hover:bg-yellow-100 rounded-full transition"><Camera size={18} /></button>
          </div>
        </form>

        <div className="flex-1 md:hidden" />

        <button type="button" onClick={() => setMobileOpen('search')} className="md:hidden p-2 text-white hover:text-yellow-300 transition shrink-0" aria-label="Buscar" data-testid="btn-mobile-search">
          <Search size={22} />
        </button>

        <Link to="/favoritos" className="relative p-2 text-white hover:text-yellow-300 transition shrink-0 hidden md:block" data-testid="btn-header-favorites">
          <Heart size={22} />
          {favIds.length > 0 && (
            <Badge className="absolute -top-0.5 -right-0.5 bg-red-500 hover:bg-red-500 text-white h-4 min-w-[16px] px-1 rounded-full text-[10px] flex items-center justify-center">
              {favIds.length}
            </Badge>
          )}
        </Link>

        <Link
          to="/mi-cuenta"
          data-testid="btn-customer-profile"
          title="Mi cuenta / Sellos"
          className="p-2 text-white hover:text-yellow-300 transition shrink-0 hidden md:block"
        >
          <User size={22} />
        </Link>

        <Button
          onClick={() => setIsOpen(true)}
          className="relative bg-[#F8D13F] text-[#1B2A4A] hover:bg-yellow-300 rounded-full h-11 px-3 md:px-5 font-semibold shrink-0"
        >
          <ShoppingCart size={18} className="md:mr-2" />
          <span className="hidden md:inline">Carrito</span>
          {count > 0 && (
            <Badge className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-500 text-white h-5 min-w-[20px] px-1 rounded-full text-[11px] flex items-center justify-center">
              {count}
            </Badge>
          )}
        </Button>
      </div>

      <nav className="hidden lg:block bg-[#1B2A4A] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4">
          <ul className="flex items-center gap-1 overflow-x-auto">
            <li>
              <Link to="/catalogo" className="inline-block px-3 py-2.5 text-sm font-semibold text-yellow-300 hover:bg-white/5 rounded">
                {L('btn.all-products', 'Todos los productos')}
              </Link>
            </li>
            {CATS.map((c) => (
              <li key={c.id}>
                <Link to={`/catalogo?cat=${c.id}`} className="inline-block px-3 py-2.5 text-sm text-white/90 hover:text-yellow-300 hover:bg-white/5 rounded whitespace-nowrap">
                  {L(`category.${c.id}`, c.name)}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {mobileOpen && createPortal(
        <div className="fixed inset-0 z-[130] lg:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[82%] max-w-xs bg-[#1B2A4A] text-white p-5 pb-10 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <Logo className="h-8" />
              <button onClick={() => setMobileOpen(false)} className="p-2 -m-2 rounded-full hover:bg-white/10" aria-label="Cerrar menú" data-testid="mobile-menu-close"><X size={24} /></button>
            </div>
            <button onClick={() => { setShowStoreSelector(true); setMobileOpen(false); }} className="w-full mb-4 bg-yellow-300/10 border border-yellow-300/30 rounded-lg p-3 text-left">
              <div className="flex items-center gap-2 text-xs text-yellow-300 font-semibold"><StoreIcon size={14} /> Mi tienda</div>
              <div className="text-sm mt-0.5">{store ? store.name : 'Elegir tienda →'}</div>
            </button>
            <form onSubmit={(e) => { submitSearch(e); setMobileOpen(false); }} className="relative mb-3">
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar..." autoFocus={mobileOpen === 'search'} data-testid="mobile-menu-search" className="pl-10 pr-20 bg-white text-slate-900 border-0" />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                <button type="button" onClick={() => { setVisualOpen('photo'); setMobileOpen(false); }} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-full"><ImageIcon size={16} /></button>
                <button type="button" onClick={() => { setVisualOpen('barcode'); setMobileOpen(false); }} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-full"><Camera size={16} /></button>
              </div>
            </form>
            <div className="text-xs uppercase tracking-wider text-yellow-300 mb-2 font-semibold">Categorías</div>
            <ul className="space-y-1">
              <li><Link onClick={() => setMobileOpen(false)} to="/catalogo" className="block py-2 px-2 rounded hover:bg-white/10 font-semibold">{L('btn.all-products', 'Todos los productos')}</Link></li>
              {CATS.map((c) => (
                <li key={c.id}>
                  <Link onClick={() => setMobileOpen(false)} to={`/catalogo?cat=${c.id}`} className="block py-2 px-2 rounded hover:bg-white/10">{L(`category.${c.id}`, c.name)}</Link>
                </li>
              ))}
              <li className="pt-3 border-t border-white/10 mt-3"><Link onClick={() => setMobileOpen(false)} to="/favoritos" className="block py-2 px-2 rounded hover:bg-white/10 flex items-center gap-2"><Heart size={16} /> {L('btn.my-favorites', 'Mis favoritos')}</Link></li>
              <li><Link onClick={() => setMobileOpen(false)} to="/mi-cuenta" className="block py-2 px-2 rounded hover:bg-white/10 flex items-center gap-2" data-testid="mobile-btn-customer-profile"><User size={16} /> {L('btn.my-account', 'Mi cuenta / Sellos')}</Link></li>
            </ul>
          </div>
        </div>,
        document.body
      )}

      {showStoreSelector && <StoreSelector forceOpen onClose={() => setShowStoreSelector(false)} />}
      {visualOpen && <VisualSearchModal mode={visualOpen} onClose={() => setVisualOpen(null)} onResult={onScanned} />}
    </header>
  );
}
